import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Button, { BUTTON_SIZES, BUTTON_VARIANTS } from "@shared/ui/Button";
import { Textarea } from "@shared/ui/Inputs";
import Label, { LABEL_SIZES } from "@shared/ui/Label";
import Toast, { TOAST_TYPES } from "@shared/ui/Toast";
import MakulaLogo from "@shared/svg/makula-logo.svg?react";
import DownloadIcon from "@shared/svg/download.svg?react";
import { CheckCircle, Info, WarningCircle } from "phosphor-react";
import React, { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FINANCE_LINE_ITEM_UNIT,
  FINANCE_PAYMENT_TERMS,
} from "~/constants/finance";

import { Loading } from "~/components/general";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/Modal";
import { deriveQuoteBreakdown } from "~/features/Quotes/detail/utils/breakdown";
import {
  calculateLineItemTotal,
  customerFacingUnitPrice,
} from "~/features/Quotes/detail/utils/lineItems";
import { isDueDateInFuture } from "~/features/Invoices/detail/utils/format";
import {
  formatQuoteAmount,
  formatQuoteDate,
  quoteCurrencyCode,
} from "~/features/Quotes/detail/utils/format";
import {
  useDownloadPublicFinanceInvoicePdf,
  useGetPublicFinanceInvoice,
  useRequestPublicFinanceInvoiceChanges,
} from "~/services/finance/publicInvoice";

const DEFAULT_BANNER = "/images/default/cover-photo.png";
const BANNER_HEIGHT = 140;
const LOGO_SIZE = 88;

const LINE_GRID =
  "grid grid-cols-[minmax(0,1.6fr)_72px_110px_110px_96px_110px] gap-sm items-center";

const UNIT_LABELS = FINANCE_LINE_ITEM_UNIT as Record<string, string>;

/** Statuses where the customer should only see a placeholder — not the invoice details. */
const PUBLIC_RESTRICTED_STATUSES = new Set(["DRAFT", "CANCELLED"]);
const PUBLIC_ACTIONABLE_STATUSES = new Set(["SENT", "PENDING_CHANGES"]);

const joinParts = (parts: Array<string | null | undefined>) =>
  parts.filter((p) => Boolean(p && String(p).trim())).join(", ");

const PublicInvoicePage = () => {
  const { oemSlug = "", token: tokenParam = "" } = useParams<{
    oemSlug: string;
    token: string;
  }>();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get("preview") === "1";
  const { messages } = useIntl();
  const t = (messages as any)?.invoices?.detail?.public;
  const token = decodeURIComponent(tokenParam);
  const { invoice, loading, refetch } = useGetPublicFinanceInvoice(
    oemSlug,
    token,
  );
  const { downloadPdf, loading: downloading } =
    useDownloadPublicFinanceInvoicePdf();
  const { requestChanges, loading: requesting } =
    useRequestPublicFinanceInvoiceChanges();

  const [changeOpen, setChangeOpen] = useState(false);
  const [changeMessage, setChangeMessage] = useState("");

  const taxPercent = useMemo(
    () =>
      (invoice?.taxes ?? []).reduce(
        (sum: number, tax: { percentage?: number | null }) =>
          sum + (tax.percentage ?? 0),
        0,
      ),
    [invoice?.taxes],
  );

  const amounts = useMemo(
    () =>
      deriveQuoteBreakdown(invoice?.lineItems, taxPercent, {
        foldMarkupIntoPrice: true,
      }),
    [invoice?.lineItems, taxPercent],
  );

  const money = quoteCurrencyCode(invoice?.oemCurrency);
  const currency = invoice?.oemCurrency;

  const publicBeforeTax = amounts.subtotal + amounts.markup - amounts.discount;
  const publicTax =
    Math.round(publicBeforeTax * (taxPercent / 100) * 100) / 100;
  const publicTotal = Math.round((publicBeforeTax + publicTax) * 100) / 100;

  const canAct = Boolean(
    !isPreview &&
      invoice &&
      PUBLIC_ACTIONABLE_STATUSES.has(invoice.status ?? ""),
  );

  const onDownload = async () => {
    try {
      const url = await downloadPdf(oemSlug, token);
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    } catch (error: any) {
      toast(
        <Toast
          type={TOAST_TYPES.ERROR}
          message={
            error?.message ?? t?.downloadError ?? "Failed to download PDF"
          }
        />,
        { closeButton: false },
      );
    }
  };

  const onSubmitChange = async () => {
    if (!changeMessage.trim()) return;
    try {
      await requestChanges({
        oemSlug,
        token,
        message: changeMessage.trim(),
      });
      toast(
        <Toast
          type={TOAST_TYPES.SUCCESS}
          message={t?.changeSuccess ?? "Change request submitted"}
        />,
        { closeButton: false },
      );
      setChangeOpen(false);
      setChangeMessage("");
      await refetch();
    } catch (error: any) {
      toast(
        <Toast
          type={TOAST_TYPES.ERROR}
          message={
            error?.message ?? t?.changeError ?? "Failed to submit request"
          }
        />,
        { closeButton: false },
      );
    }
  };

  if (loading) return <Loading />;

  if (!invoice) {
    return (
      <div className="h-screen overflow-y-auto flex items-center justify-center p-xl bg-accent">
        <BodyText size={BODY_TEXT_SIZES.MEDIUM}>
          {t?.notFound ?? "Invoice not found"}
        </BodyText>
      </div>
    );
  }

  const sender = invoice.senderDetails;
  const createdBy = invoice.createdBy;
  const companyName = invoice.company?.name ?? "—";
  const senderCompany = sender?.companyName || invoice.oemName || companyName;
  const senderAddress = joinParts([
    sender?.streetAddress,
    joinParts([sender?.postalCode, sender?.city]),
    sender?.country,
  ]);
  const note = invoice.customerNotes || "";
  const bannerUrl = invoice.oemCover || DEFAULT_BANNER;
  const logoUrl = invoice.oemLogo;

  const totalDisplay = formatQuoteAmount(publicTotal, currency);
  const status = invoice.status ?? "";
  const isRestricted = PUBLIC_RESTRICTED_STATUSES.has(status);
  const isPaid = status === "PAID";
  const isOverdue = status === "OVERDUE";
  const dueDatePast =
    Boolean(invoice.dueDate) && !isDueDateInFuture(invoice.dueDate);
  const payment = invoice.paymentInformation;
  const paymentTermsKey = invoice.documentDefaults?.paymentTerms;
  const paymentTermsLabel = paymentTermsKey
    ? (FINANCE_PAYMENT_TERMS as Record<string, string>)[paymentTermsKey] ??
      paymentTermsKey
    : null;
  const footerSender = joinParts([senderCompany, senderAddress || null]);
  const footerPaymentLines = [
    paymentTermsLabel
      ? `${t?.paymentTerms ?? "Payment Terms"}: ${paymentTermsLabel}`
      : null,
    payment?.vatId ? `${t?.vatId ?? "VAT ID"}: ${payment.vatId}` : null,
    payment?.taxNumber
      ? `${t?.taxNumber ?? "Tax Number"}: ${payment.taxNumber}`
      : null,
    payment?.bankName
      ? `${t?.bankName ?? "Bank Name"}: ${payment.bankName}`
      : null,
    payment?.accountHolderName
      ? `${t?.accountHolderName ?? "Account Holder Name"}: ${
          payment.accountHolderName
        }`
      : null,
    payment?.accountNumber
      ? `${t?.accountNumber ?? "Account Number"}: ${payment.accountNumber}`
      : null,
    payment?.routingNumber
      ? `${t?.routingNumber ?? "Routing Number / ABA"}: ${
          payment.routingNumber
        }`
      : null,
    payment?.iban ? `${t?.iban ?? "IBAN"}: ${payment.iban}` : null,
    payment?.bicSwift ? `${t?.bic ?? "BIC/SWIFT"}: ${payment.bicSwift}` : null,
  ].filter((line): line is string => Boolean(line && String(line).trim()));
  const footerLines = [footerSender, ...footerPaymentLines].filter(
    (line): line is string => Boolean(line && String(line).trim()),
  );

  const statusBanner = (() => {
    if (isPaid) {
      return {
        tone: "success" as const,
        icon: (
          <CheckCircle
            size={16}
            className="text-success shrink-0"
            weight="fill"
          />
        ),
        message: t?.paidBanner ?? "This invoice has been paid",
      };
    }
    if (status === "PENDING_CHANGES") {
      return {
        tone: "warning" as const,
        icon: <Info size={16} className="text-warning shrink-0" />,
        message: t?.pendingChangesBanner ?? "Your change request has been sent",
      };
    }
    if (isOverdue) {
      return {
        tone: "warning" as const,
        icon: <Info size={16} className="text-warning shrink-0" />,
        message: t?.overdueBanner ?? "This invoice is overdue",
      };
    }
    if (status === "CANCELLED") {
      return {
        tone: "warning" as const,
        icon: <Info size={16} className="text-warning shrink-0" />,
        message: t?.cancelledBanner ?? "This invoice is no longer available",
      };
    }
    if (status === "DRAFT") {
      return {
        tone: "warning" as const,
        icon: <Info size={16} className="text-warning shrink-0" />,
        message: t?.draftBanner ?? "This invoice is being prepared",
      };
    }
    return null;
  })();

  return (
    <div className="h-screen overflow-y-auto bg-[#F7F8FA]">
      {isPreview ? (
        <div className="sticky top-0 z-20 flex items-center justify-center gap-sm bg-yellow-light p-sm">
          <Info className="text-yellow h-xl w-xl shrink-0" />
          <BodyText
            size={BODY_TEXT_SIZES.X_SMALL}
            color="text-primary"
            className="text-center"
          >
            {t?.previewBanner ??
              "Preview Mode: Actions are disabled in preview"}
          </BodyText>
        </div>
      ) : null}
      <div className="relative">
        <div
          className="w-full overflow-hidden bg-secondary"
          style={{ height: BANNER_HEIGHT }}
        >
          <img src={bannerUrl} alt="" className="w-full h-full object-cover" />
        </div>
        <div
          className="absolute left-xl md:left-[max(1.5rem,calc((100%-1120px)/2+1.5rem))] border-2 border-solid border-white rounded-lg overflow-hidden bg-white shadow-sm"
          style={{
            width: LOGO_SIZE,
            height: LOGO_SIZE,
            top: BANNER_HEIGHT - LOGO_SIZE / 2,
          }}
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={senderCompany}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-secondary">
              <MakulaLogo className="w-fit h-4xl flex-shrink-0" />
            </div>
          )}
        </div>
      </div>

      <div
        className="max-w-[1120px] mx-auto px-xl pb-[160px]"
        style={{ paddingTop: LOGO_SIZE / 2 + 24 }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px] gap-2xl items-start">
          <div className="bg-primary rounded-xl border border-solid border-primary overflow-hidden shadow-sm">
            <div
              className={`p-xl flex flex-col gap-xl ${
                isRestricted ? "min-h-[360px]" : ""
              }`}
            >
              {statusBanner ? (
                <div
                  className={`flex h-8 items-center gap-sm rounded-lg px-md ${
                    statusBanner.tone === "success"
                      ? "bg-success-lightest"
                      : "bg-warning-lightest"
                  }`}
                >
                  {statusBanner.icon}
                  <BodyText
                    size={BODY_TEXT_SIZES.X_SMALL}
                    color="text-primary"
                    className="leading-snug"
                  >
                    {statusBanner.message}
                  </BodyText>
                </div>
              ) : null}

              {!isRestricted ? (
                <>
                  <div className="overflow-x-auto">
                    <div className="min-w-[720px] flex flex-col gap-sm">
                      <div
                        className={`${LINE_GRID} px-md py-sm rounded-lg bg-accent`}
                      >
                        {[
                          t?.item ?? "Item / Service",
                          t?.qty ?? "Qty",
                          t?.unit ?? "Unit",
                          `${t?.price ?? "Price"} (${money})`,
                          t?.discount ?? "Discount",
                          t?.total ?? "Total",
                        ].map((label) => (
                          <BodyText
                            key={label}
                            size={BODY_TEXT_SIZES.X_SMALL}
                            color="text-secondary"
                            className="whitespace-nowrap"
                          >
                            {label}
                          </BodyText>
                        ))}
                      </div>

                      {(invoice.lineItems ?? []).map(
                        (
                          row: {
                            _id?: string;
                            name?: string;
                            quantity?: number;
                            unit?: string;
                            unitPrice?: number;
                            discountPercent?: number;
                            markupPercent?: number;
                          },
                          index: number,
                        ) => {
                          const unitKey = row.unit ?? "EACH";
                          const total = calculateLineItemTotal(row);
                          const displayPrice = customerFacingUnitPrice(row);
                          return (
                            <div
                              key={row._id ?? `row-${index}`}
                              className={`${LINE_GRID} px-md py-sm`}
                            >
                              <BodyText size={BODY_TEXT_SIZES.SMALL}>
                                {row.name || "—"}
                              </BodyText>
                              <BodyText size={BODY_TEXT_SIZES.SMALL}>
                                {row.quantity ?? 0}
                              </BodyText>
                              <BodyText size={BODY_TEXT_SIZES.SMALL}>
                                {UNIT_LABELS[unitKey] ?? unitKey}
                              </BodyText>
                              <BodyText size={BODY_TEXT_SIZES.SMALL}>
                                {money}{" "}
                                {Number.isInteger(displayPrice)
                                  ? displayPrice
                                  : displayPrice.toFixed(2)}
                              </BodyText>
                              <BodyText size={BODY_TEXT_SIZES.SMALL}>
                                {row.discountPercent ?? 0}%
                              </BodyText>
                              <BodyText size={BODY_TEXT_SIZES.SMALL}>
                                {money}{" "}
                                {Number.isInteger(total)
                                  ? total
                                  : total.toFixed(2)}
                              </BodyText>
                            </div>
                          );
                        },
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-sm max-w-[280px] ml-auto w-full">
                    <BreakdownRow
                      label={t?.subtotal ?? "Sub Total"}
                      value={formatQuoteAmount(amounts.subtotal, currency)}
                    />
                    <BreakdownRow
                      label={t?.discount ?? "Discount"}
                      value={formatQuoteAmount(amounts.discount, currency)}
                    />
                    {(invoice.taxes ?? []).length > 0 ? (
                      (invoice.taxes ?? []).map(
                        (tax: {
                          _id?: string;
                          name?: string;
                          percentage?: number;
                        }) => (
                          <BreakdownRow
                            key={tax._id ?? tax.name}
                            label={`${tax.name ?? t?.tax ?? "Tax"} (${
                              tax.percentage ?? 0
                            }%)`}
                            value={formatQuoteAmount(
                              publicBeforeTax * ((tax.percentage ?? 0) / 100),
                              currency,
                            )}
                          />
                        ),
                      )
                    ) : (
                      <BreakdownRow
                        label={`${t?.tax ?? "Tax"} (0%)`}
                        value={formatQuoteAmount(0, currency)}
                      />
                    )}
                    <BreakdownRow
                      label={t?.total ?? "Total"}
                      value={totalDisplay}
                      strong
                    />
                  </div>
                </>
              ) : (
                <div className="flex-1" />
              )}

              {note ? (
                <BodyText
                  size={BODY_TEXT_SIZES.SMALL}
                  className="text-secondary"
                >
                  <span className="font-medium text-primary">
                    {t?.notes ?? "Note"}:
                  </span>{" "}
                  {note}
                </BodyText>
              ) : null}
            </div>

            {footerLines.length > 0 ? (
              <div className="bg-[#F4F5F7] px-xl py-md flex flex-col gap-xs min-w-0">
                {footerLines.map((line) => (
                  <BodyText
                    key={line}
                    size={BODY_TEXT_SIZES.X_SMALL}
                    className="text-secondary break-words [overflow-wrap:anywhere]"
                  >
                    {line}
                  </BodyText>
                ))}
              </div>
            ) : null}
          </div>

          <aside className="flex flex-col gap-lg pt-xs min-w-0 overflow-hidden">
            <div className="flex flex-col gap-md">
              <SidebarMeta
                label={t?.invoiceNumber ?? "Invoice number"}
                value={invoice.invoiceNumber}
              />
              <SidebarMeta
                label={t?.invoiceDate ?? "Invoice Date"}
                value={
                  invoice.issueDate ? formatQuoteDate(invoice.issueDate) : "—"
                }
              />
              <div>
                <BodyText
                  size={BODY_TEXT_SIZES.X_SMALL}
                  className="text-secondary mb-xs"
                >
                  {t?.dueDate ?? "Due Date"}
                </BodyText>
                {dueDatePast ? (
                  <div className="flex items-center gap-xs text-[#E11D48]">
                    <WarningCircle size={16} weight="fill" />
                    <BodyText
                      size={BODY_TEXT_SIZES.SMALL}
                      className="text-[#E11D48] font-medium"
                    >
                      {formatQuoteDate(invoice.dueDate)}
                    </BodyText>
                  </div>
                ) : (
                  <Label size={LABEL_SIZES.SMALL}>
                    {invoice.dueDate ? formatQuoteDate(invoice.dueDate) : "—"}
                  </Label>
                )}
              </div>
            </div>

            <div className="border-0 border-t border-solid border-primary pt-md flex flex-col gap-md">
              <SidebarBlock
                label={t?.from ?? "From"}
                lines={[createdBy?.name, createdBy?.email, createdBy?.phone]}
              />
              <SidebarBlock
                label={t?.company ?? "Company"}
                lines={[senderCompany, senderAddress]}
              />
              <SidebarBlock
                label={t?.sentTo ?? "Sent To"}
                lines={[
                  companyName,
                  invoice.company?.address,
                  invoice.company?.website,
                ]}
              />
            </div>

            <div className="flex flex-col gap-sm pt-sm">
              {isPaid ? (
                <Button
                  size={BUTTON_SIZES.NORMAL}
                  variant={BUTTON_VARIANTS.OUTLINE}
                  text={t?.downloadPdf ?? "PDF"}
                  leadingIcon={<DownloadIcon width={16} height={16} />}
                  disabled={downloading || isRestricted}
                  onClick={() => void onDownload()}
                  fullWidth
                />
              ) : (
                <div className="grid grid-cols-2 gap-sm">
                  <Button
                    size={BUTTON_SIZES.NORMAL}
                    variant={BUTTON_VARIANTS.OUTLINE}
                    text={t?.downloadPdf ?? "PDF"}
                    leadingIcon={<DownloadIcon width={16} height={16} />}
                    disabled={downloading || isRestricted}
                    onClick={() => void onDownload()}
                    fullWidth
                  />
                  <Button
                    size={BUTTON_SIZES.NORMAL}
                    variant={BUTTON_VARIANTS.OUTLINE}
                    text={t?.requestChange ?? "Request Change"}
                    disabled={!canAct}
                    onClick={() => setChangeOpen(true)}
                    fullWidth
                  />
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>

      <Dialog open={changeOpen} onOpenChange={setChangeOpen}>
        <DialogContent maxWidth="max-w-[520px]">
          <DialogHeader>
            <DialogTitle>
              {t?.requestChangeTitle ?? "Request Changes"}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Textarea
              label={
                t?.requestChangeMessage ?? "Describe the changes you'd like"
              }
              rows={6}
              className="!h-40"
              value={changeMessage}
              onChange={(e) => setChangeMessage(e.target.value)}
              placeholder={t?.requestChangePlaceholder ?? "Enter your text"}
            />
          </DialogBody>
          <DialogFooter>
            <Button
              variant={BUTTON_VARIANTS.OUTLINE}
              text={(messages as any)?.common?.cancel ?? "Cancel"}
              onClick={() => setChangeOpen(false)}
            />
            <Button
              text={t?.submitRequest ?? "Submit Request"}
              disabled={!changeMessage.trim() || requesting}
              onClick={() => void onSubmitChange()}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const BreakdownRow: React.FC<{
  label: string;
  value: string;
  strong?: boolean;
}> = ({ label, value, strong }) => (
  <div className="flex items-center justify-between gap-md">
    <BodyText
      size={BODY_TEXT_SIZES.X_SMALL}
      color={strong ? "text-primary" : "text-secondary"}
      className={strong ? "font-semibold" : undefined}
    >
      {label}
    </BodyText>
    <BodyText
      size={BODY_TEXT_SIZES.X_SMALL}
      color="text-primary"
      className={strong ? "font-semibold" : undefined}
    >
      {value}
    </BodyText>
  </div>
);

const SidebarMeta: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div className="min-w-0">
    <BodyText size={BODY_TEXT_SIZES.X_SMALL} className="text-secondary mb-xs">
      {label}
    </BodyText>
    <Label
      size={LABEL_SIZES.SMALL}
      className="break-words [overflow-wrap:anywhere]"
    >
      {value}
    </Label>
  </div>
);

const SidebarBlock: React.FC<{
  label: string;
  lines: Array<string | null | undefined>;
}> = ({ label, lines }) => {
  const visible = lines.filter((line) => Boolean(line && String(line).trim()));
  if (!visible.length) return null;
  return (
    <div className="border-0 border-b border-solid border-primary pb-md last:border-b-0 last:pb-0 min-w-0">
      <BodyText size={BODY_TEXT_SIZES.X_SMALL} className="text-secondary mb-xs">
        {label}
      </BodyText>
      <div className="flex flex-col gap-[2px] min-w-0">
        {visible.map((line, index) => (
          <BodyText
            key={`${label}-${index}`}
            size={BODY_TEXT_SIZES.SMALL}
            className="break-words [overflow-wrap:anywhere]"
          >
            {line}
          </BodyText>
        ))}
      </div>
    </div>
  );
};

export default PublicInvoicePage;
